import { Component, HostListener } from '@angular/core';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';
import { THEMES } from 'src/app/data/product/product.data';
import { PRODUCT_HEROES } from '../product-hero.data';
import { ProductShots, Shot } from '../product-shots';

// Promo landing page for the All In One Themes Chrome extension, served at
// /plugins/chrome/all-in-one-themes. Reuses the color-picker page styles. SEO is
// applied by the NavigationInterceptor from navigation.data.ts (client) and
// scripts/seo-route-meta.js (server).
@Component( {
  selector: 'app-all-in-one-themes-page',
  templateUrl: './all-in-one-themes-page.component.html',
  styleUrls: [
    '../chrome-color-picker-page/chrome-color-picker-page.component.scss',
    '../macos-tidy-desktop-page/macos-tidy-desktop-page.component.scss',
    '../product-page.scss',
  ],
} )
export class AllInOneThemesPageComponent extends ProductShots {

  readonly storeUrl = 'https://chromewebstore.google.com/detail/iekicoldppmopekekolhdoofncnhhbeh';
  // The product on its stage at the top of the page (product-hero.data.ts), and the facts as
  // chips under the pitch.
  readonly hero = PRODUCT_HEROES[ 'all-in-one-themes' ];
  readonly facts = [ 'Free', 'No Sign In, No Tracking', 'Twelve Themes, One Popup' ];


  readonly features = [
    {
      title: '12 Themes',
      description: 'A collection of free themes inspired by nature and iconic cities, from calm light palettes to deep, immersive dark ones.',
    },
    {
      title: 'Nature and Cities',
      description: 'Aurora Borealis, Everest, Sakura, Sequoia, Istanbul, Tokyo, Paris and more.',
    },
    {
      title: 'Preview in the Popup',
      description: 'See each theme on a Chrome mockup before you apply it, right inside the popup.',
    },
    {
      title: 'One Place to Browse',
      description: 'Pick a theme and add it to Chrome from a single popup, no hunting through the store.',
    },
    {
      title: 'Skins the Site',
      description: 'On codigrate.com the site can match its own look to your active Codigrate browser theme.',
    },
    {
      title: 'Free',
      description: 'Every theme is free. No sign in, no clutter, no tracking.',
    },
  ];

  // Three of them under the product's picture (the Apps & Plugins layout, ../product-page.scss),
  // the others after the themes.
  readonly highlights = [ 0, 2, 3 ].map( i => this.features[ i ] );
  readonly more = [ 1, 4, 5 ].map( i => this.features[ i ] );

  // Its Chrome Web Store pictures (the ones its listing shows), under Screenshots: the windows,
  // the popup in light and dark; each opens in the lightbox. They are 1280×800, the store's own
  // size.
  readonly shots: Shot[] = [
    {
      name: 'Twelve Themes',
      src: './assets/product-lines/tools/all-in-one-themes/screenshot-twelve-themes.webp',
      large: './assets/product-lines/tools/all-in-one-themes/screenshot-twelve-themes.webp',
      alt: 'Twelve themes, one popup: Chrome windows fanned out, each in another Codigrate theme',
    },
    {
      name: 'Preview Before You Apply',
      src: './assets/product-lines/tools/all-in-one-themes/screenshot-popup-light.webp',
      large: './assets/product-lines/tools/all-in-one-themes/screenshot-popup-light.webp',
      alt: 'Preview before you apply: the All In One Themes popup showing the Everest theme on a Chrome mockup',
    },
    {
      name: 'Light And Dark Themes',
      src: './assets/product-lines/tools/all-in-one-themes/screenshot-popup-dark.webp',
      large: './assets/product-lines/tools/all-in-one-themes/screenshot-popup-dark.webp',
      alt: 'Light and dark themes: the popup showing the dark Aurora Borealis theme on a Chrome mockup',
    },
  ];

  // Every theme that ships a Chrome version (the ones bundled in the extension),
  // with its Chrome screenshot, name and Web Store link. Built from THEMES so it
  // stays in sync as themes are added.
  readonly chromeThemes = THEMES
    .reduce( ( all, line ) => all.concat( line.products || [] ), [] as any[] )
    .filter( ( p: any ) => p.key !== 'all-in-one' && p.key !== 'all-in-one-themes' )
    .map( ( p: any ) => {
      const chrome = ( p.platforms || [] ).find(
        ( pl: any ) => ( pl.url || '' ).includes( 'chromewebstore.google.com' ) && pl.status !== 'planned',
      );
      return chrome && chrome.screenshot
        ? { name: p.name, screenshot: chrome.screenshot.src, logo: p.logo, url: chrome.url, appearance: p.appearance }
        : null;
    } )
    .filter( Boolean )
    // Light themes first, then dark (stable within each group).
    .sort( ( a: any, b: any ) => ( a.appearance === 'Light' ? 0 : 1 ) - ( b.appearance === 'Light' ? 0 : 1 ) );

  // The theme whose screenshot is enlarged in the lightbox, or null when closed.
  lightboxTheme: any = null;

  constructor( private analyticsService: AnalyticsService ) {
    super();
  }

  onInstall(): void {
    this.analyticsService.logEvent( 'Extension Install Clicked', { extension: 'all-in-one-themes' } );
  }

  // Both lightboxes count for the Tab cycle (ProductShots).
  protected override get lightboxOpen(): boolean {
    return !!this.lightbox || !!this.lightboxTheme;
  }

  openTheme( theme: any ): void {
    this.rememberOpener();
    this.lightboxTheme = theme;
    this.focusLightbox();
  }

  closeTheme(): void {
    this.lightboxTheme = null;
    this.restoreOpener();
  }

  @HostListener( 'document:keydown.escape' )
  onThemeEscape(): void {
    if ( this.lightboxTheme ) {
      this.closeTheme();
    }
  }
}
